import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FeatureService } from '@maximizer/core/shared/data-access';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import {
  OUTLOOK_SYNC_CALENDAR_ROLLING_WINDOW_FEATURE_FLAG,
  OUTLOOK_SYNC_FREQUENCY,
  OutlookSyncConfigurationPayload,
  OutlookSyncHistoryEntry,
  OutlookSyncType as OutlookSyncApiType,
  UserSyncConfiguration,
  UserSyncConfigurationService,
} from '@maximizer/outlook-sync/data-access';
import { OutlookSyncFormGroup } from '../../../form.model';
import { HistoryGridComponent } from '../../components/history-grid/history-grid.component';
import {
  loadSyncHistory,
  resolveConfigurationId,
} from '../../utils/history-mapper.util';

@Component({
  selector: 'maximizer-outlook-calendar-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropDownListModule,
    TranslateModule,
    SwitchModule,
    TooltipModule,
    HistoryGridComponent,
  ],
  templateUrl: './outlook-calendar-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OutlookCalendarTabComponent implements OnInit {
  private readonly outlookSync = inject(UserSyncConfigurationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly feature = inject(FeatureService);

  configuration = input.required<UserSyncConfiguration | null>();
  form = input.required<OutlookSyncFormGroup>();
  payload = input.required<OutlookSyncConfigurationPayload | null>();

  protected readonly history = signal<OutlookSyncHistoryEntry[]>([]);
  protected readonly historyLoading = signal(false);

  protected readonly syncUpToOptions: ReadonlyArray<number> = [
    30, 90, 180, 365,
  ];

  protected readonly syncFrequency = OUTLOOK_SYNC_FREQUENCY.calendar;

  protected readonly daysAfter = signal<number>(
    OUTLOOK_SYNC_FREQUENCY.calendar.defaultDaysAfter,
  );

  private currentConfigurationId: string | null = null;

  constructor() {
    effect(() => {
      this.configuration();
      this.payload();
      this.handleConfigurationChange();
    });
  }

  ngOnInit(): void {
    const control = this.form().controls.appointmentsRollingWindowDaysAfter;
    this.syncRollingWindowDaysAfter();
    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.setDaysAfter(value));
  }

  protected refreshHistory(): void {
    const configurationId = this.getConfigurationId();
    loadSyncHistory({
      service: this.outlookSync,
      destroyRef: this.destroyRef,
      syncType: OutlookSyncApiType.Appointments,
      idPrefix: 'appointments-history',
      errorMessage: 'Failed to load calendar sync history',
      configurationId,
      isCurrent: () => this.currentConfigurationId === configurationId,
      history: this.history,
      loading: this.historyLoading,
    });
  }

  private handleConfigurationChange(): void {
    const configurationId = this.getConfigurationId();

    if (!configurationId) {
      this.currentConfigurationId = null;
      this.resetState();
      return;
    }

    if (configurationId === this.currentConfigurationId) {
      this.syncAppointmentsToggle();
      this.syncRollingWindowDaysAfter();
      return;
    }

    this.currentConfigurationId = configurationId;
    this.syncAppointmentsToggle();
    this.syncRollingWindowDaysAfter();
    this.refreshHistory();
  }

  private resetState(): void {
    this.history.set([]);
    this.historyLoading.set(false);
    this.form().controls.appointmentsSyncEnabled.setValue(false, {
      emitEvent: false,
    });
    this.syncRollingWindowDaysAfter();
  }

  private getConfigurationId(): string | null {
    return resolveConfigurationId(this.configuration(), this.payload());
  }

  private syncAppointmentsToggle(): void {
    const control = this.form().controls.appointmentsSyncEnabled;
    const enabled =
      this.configuration()?.appointmentsSync?.enabled ??
      this.payload()?.appointmentsSyncEnabled ??
      control.value ??
      false;
    control.setValue(enabled, { emitEvent: false });
  }

  private syncRollingWindowDaysAfter(): void {
    this.setDaysAfter(
      this.form().controls.appointmentsRollingWindowDaysAfter.value,
    );
  }

  private setDaysAfter(value: number | null | undefined): void {
    const defaultDaysAfter = OUTLOOK_SYNC_FREQUENCY.calendar.defaultDaysAfter;
    const showRollingWindowDaysAfter = this.showRollingWindowDaysAfter;
    const daysAfter = showRollingWindowDaysAfter
      ? (value ?? defaultDaysAfter)
      : defaultDaysAfter;

    if (
      !showRollingWindowDaysAfter &&
      this.form().controls.appointmentsRollingWindowDaysAfter.value !==
        daysAfter
    ) {
      this.form().controls.appointmentsRollingWindowDaysAfter.setValue(
        daysAfter,
        { emitEvent: false },
      );
    }

    this.daysAfter.set(daysAfter);
  }

  protected get showRollingWindowDaysAfter(): boolean {
    return this.feature.isFeatureOn(
      OUTLOOK_SYNC_CALENDAR_ROLLING_WINDOW_FEATURE_FLAG,
      false,
    );
  }
}
